// Shared Geo utilities: parse geometry and normalize API responses for regions and councils

// Parse geometry that may be a stringified GeoJSON, a GeoJSON geometry, a Feature, or opaque
export const parseGeom = (g) => {
  if (!g) return null;
  if (typeof g === "string") {
    try {
      return JSON.parse(g);
    } catch {
      return null;
    }
  }
  if (g && g.type && g.coordinates) return g; // GeoJSON geometry
  if (g && g.geometry) return g.geometry; // Feature-like
  return null;
};

// Normalize various API response shapes into a uniform array of { id, name, geometry, properties }
export const normalizeRegionsResponse = (res) => {
  const fromGeojson = (rec) => {
    const gj = rec?.geojson;
    if (!gj) return null;
    if (Array.isArray(gj?.features)) {
      const f = gj.features[0];
      return {
        id: f?.id || f?.properties?.id || rec?.id || rec?.code,
        name: f?.properties?.name || f?.properties?.NAME || f?.properties?.Name || rec?.name,
        geometry: f?.geometry,
        properties: f?.properties || rec?.properties || {},
      };
    }
    if (gj?.type === "Feature" && gj?.geometry) {
      return {
        id: gj?.id || gj?.properties?.id || rec?.id || rec?.code,
        name: gj?.properties?.name || gj?.properties?.NAME || gj?.properties?.Name || rec?.name,
        geometry: gj?.geometry,
        properties: gj?.properties || rec?.properties || {},
      };
    }
    const geometry = parseGeom(gj?.geometry || gj);
    if (geometry) {
      return {
        id: rec?.id || rec?.code || rec?.properties?.id,
        name: rec?.name || rec?.properties?.name || rec?.properties?.NAME || rec?.properties?.Name,
        geometry,
        properties: rec?.properties || {},
      };
    }
    return null;
  };

  const mapRecord = (rec) => {
    const fromGj = fromGeojson(rec);
    if (fromGj) return fromGj;
    return {
      id: rec?.id || rec?.code || rec?.properties?.id,
      name:
        rec?.name ||
        rec?.properties?.name ||
        rec?.properties?.NAME ||
        rec?.properties?.Name,
      geometry: parseGeom(rec?.geom || rec?.geometry),
      properties: rec?.properties || {},
    };
  };

  if (Array.isArray(res)) return res.map(mapRecord);
  if (Array.isArray(res?.features))
    return res.features.map((f) => ({
      id: f.id || f.properties?.id,
      name: f.properties?.name || f.properties?.NAME || f.properties?.Name,
      geometry: f.geometry,
      properties: f.properties,
    }));
  if (Array.isArray(res?.data)) return res.data.map(mapRecord);
  // Support single object in res.data
  if (res?.data && !Array.isArray(res.data) && typeof res.data === "object")
    return [mapRecord(res.data)];
  if (Array.isArray(res?.regions)) return res.regions.map(mapRecord);
  if (res && typeof res === "object") return [mapRecord(res)];
  return [];
};

// Normalize councils response to array of { id, name, geometry, properties }
export const normalizeCouncilsResponse = (res) => {
  const mapFeature = (f, fallback) => ({
    id:
      f?.id ||
      f?.properties?.id ||
      fallback?.id ||
      fallback?.lgacode ||
      fallback?.district_id,
    name:
      fallback?.name ||
      f?.properties?.Authority ||
      f?.properties?.name ||
      f?.properties?.DISTRICT,
    geometry: f?.geometry || parseGeom(f?.geometry),
    properties: f?.properties || fallback?.properties || {},
  });

  const mapRecord = (rec) => {
    const gj = rec?.geojson;
    if (!gj) {
      return {
        id: rec?.id || rec?.lgacode || rec?.district_id,
        name: rec?.name,
        geometry: null,
        properties: rec || {},
      };
    }
    if (Array.isArray(gj?.features)) {
      const f = gj.features[0];
      return mapFeature(f, rec);
    }
    if (gj?.type === "Feature" && gj?.geometry) {
      return mapFeature(gj, rec);
    }
    const geometry = parseGeom(gj?.geometry || gj);
    return {
      id: rec?.id || rec?.lgacode || rec?.district_id,
      name: rec?.name,
      geometry,
      properties: gj?.properties || rec || {},
    };
  };

  if (Array.isArray(res)) return res.map(mapRecord);
  if (Array.isArray(res?.data)) return res.data.map(mapRecord);
  // Support single object in res.data
  if (res?.data && !Array.isArray(res.data) && typeof res.data === "object")
    return [mapRecord(res.data)];
  if (Array.isArray(res?.features)) return res.features.map((f) => mapFeature(f));
  if (res && typeof res === "object") return [mapRecord(res)];
  return [];
};
